<div class="comm-call-summary">
  <app-loader *ngIf="isBusy || saving"></app-loader>

  <ng-container *ngIf="!isBusy && summary">
    <div class="summary-header-bar">
      <div class="header-content">
        <div class="header-top">
          <span class="header-name">{{ customerName || summary.FacilityName }}</span>
          <span class="header-category-pill" *ngIf="summary.CallCategories?.length">
            {{ summary.CallCategories[0].Name }}
          </span>
        </div>
        <div class="header-subtitle">
          {{ summary.FacilityName }} &middot; {{ communicationCenterService.formatCallDateTime(summary.CallDateTime, 'EEE, MMM d, y') }}<ng-container *ngIf="summary.CallDateTime"> at {{ communicationCenterService.formatCallDateTime(summary.CallDateTime, 'h:mm a') }}</ng-container>
        </div>
      </div>
      <button type="button" class="btn-close-summary" aria-label="Close" (click)="requestClose()">
        <i class="bi bi-x-lg"></i>
      </button>
    </div>

    <div class="summary-body">
      <!-- FR-11: state plainly rather than an empty/broken view -->
      <div *ngIf="summary.DataExpired" class="alert alert-warning mt-3">
        RingCentral's data for this call has expired and no SSM-side archive is available.
      </div>

      <ng-container *ngIf="!summary.DataExpired">
        <div class="info-grid-card">
          <div class="info-item">
            <div class="info-label">Direction</div>
            <div class="info-value">
              <i class="bi" [ngClass]="summary.Direction === 'Outbound' ? 'bi-arrow-up-right' : 'bi-arrow-down-left'"></i>
              {{ summary.Direction }}
            </div>
          </div>
          <div class="info-item">
            <div class="info-label">Duration</div>
            <div class="info-value">{{ formatDuration(durationSeconds) }}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Employee / User</div>
            <div class="info-value">{{ agentName || '-' }}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Unit</div>
            <div class="info-value">{{ unitNumber || '-' }}<span *ngIf="unitDimensions"> &middot; {{ unitDimensions }}</span></div>
          </div>
          <div class="info-item">
            <div class="info-label">Unit Location</div>
            <div class="info-value">{{ summary.FacilityName || '-' }}</div>
          </div>
        </div>

        <div class="ai-summary-card" *ngIf="summary.AiSummary">
          <div class="ai-summary-title">AI Summary</div>
          <p class="ai-summary-text">{{ summary.AiSummary }}</p>
        </div>

        <div class="audio-player-card">
          <div *ngIf="!audioLoading && audioUrl" class="custom-audio-player">
            <audio #audioPlayer [src]="audioUrl" (play)="onAudioPlay()" (pause)="onAudioPause()"
              (loadedmetadata)="onLoadedMetadata()" (timeupdate)="onTimeUpdate()" (ended)="onAudioPause()"></audio>
            <button type="button" class="btn-play-pause" (click)="togglePlay()" [attr.aria-label]="isPlaying ? 'Pause' : 'Play'">
              <i class="bi" [ngClass]="isPlaying ? 'bi-pause-fill' : 'bi-play-fill'"></i>
            </button>
            <div class="waveform-track" #progressTrack (click)="seekAudio($event, progressTrack)">
              <ng-container *ngIf="waveformBars.length; else flatProgressBar">
                <span class="wave-bar" *ngFor="let bar of waveformBars; let i = index"
                  [style.height.%]="bar * 100"
                  [class.played]="(i / waveformBars.length) * 100 <= audioProgressPercent"></span>
              </ng-container>
              <ng-template #flatProgressBar>
                <div class="audio-progress-fill" [style.width.%]="audioProgressPercent"></div>
              </ng-template>
            </div>
            <span class="audio-time">{{ formatAudioTime(audioCurrentTime) }} / {{ formatAudioTime(audioDuration) }}</span>
          </div>
          <div *ngIf="!audioLoading && audioUnavailable" class="text-muted">Recording not available.</div>
        </div>

        <a *ngIf="!audioLoading && audioUrl" class="btn-download-recording" [href]="audioUrl" download target="_blank" rel="noopener">
          <i class="bi bi-download"></i> Download
        </a>

        <div class="category-tag-chips-row" *ngIf="summary.CallCategories?.length || summary.CallTags?.length">
          <div class="category-tag-col">
            <div class="chip-row" *ngIf="summary.CallCategories?.length">
              <span class="tag-chip" *ngFor="let category of summary.CallCategories">
                {{ category.Name }} <i class="bi bi-x chip-remove" (click)="removeCategory(category.Id)"></i>
              </span>
            </div>
          </div>
          <div class="category-tag-col">
            <div class="chip-row" *ngIf="summary.CallTags?.length">
              <span class="tag-chip" *ngFor="let tag of summary.CallTags">
                {{ tag.Name }} <i class="bi bi-x chip-remove" (click)="removeTag(tag.Id)"></i>
              </span>
            </div>
          </div>
        </div>

        <div class="category-tag-row">
          <div class="category-tag-col">
            <div class="category-tag-controls">
              <span class="inline-label">Categories:</span>
              <select class="category-tag-select" [(ngModel)]="selectedCategoryId">
                <option [ngValue]="null">Add Category</option>
                <option *ngFor="let c of availableCategories" [ngValue]="c.Id">{{ c.Name }}</option>
              </select>
              <button ejs-button cssClass="e-small btn-add-category-tag" [disabled]="!selectedCategoryId" (click)="addCategory()">Add</button>
            </div>
          </div>
          <div class="category-tag-col">
            <div class="category-tag-controls">
              <span class="inline-label">Tags:</span>
              <select class="category-tag-select" [(ngModel)]="selectedTagId">
                <option [ngValue]="null">Add Tag</option>
                <option *ngFor="let t of availableTags" [ngValue]="t.Id">{{ t.Name }}</option>
              </select>
              <button ejs-button cssClass="e-small btn-add-category-tag" [disabled]="!selectedTagId" (click)="addTag()">Add</button>
            </div>
          </div>
        </div>

        <div class="summary-tabs-row">
          <div class="summary-tabs">
            <button type="button" class="tab-btn" [class.active]="activeTab === 'scoring'" (click)="activeTab = 'scoring'">Scoring</button>
            <button type="button" class="tab-btn" [class.active]="activeTab === 'transcript'" (click)="openTranscriptTab()">Transcript</button>
          </div>
        </div>

        <div *ngIf="activeTab === 'scoring'" class="scores-card">
          <h6 class="section-title">Call Scores</h6>
          <div class="section-subtitle">AI score from conversation analysis; Human score from manager review</div>
          <div class="score-tiles">
            <div class="score-tile">
              <div class="score-label">AI Score</div>
              <div class="score-value ai-score">{{ summary.AiScore ?? '-' }}</div>
              <div class="score-sublabel">automated</div>
            </div>
            <div class="score-tile">
              <div class="score-label">Human Score</div>
              <div class="score-value human-score">{{ summary.HumanScore ?? '-' }}</div>
              <div class="score-sublabel">manager review</div>
            </div>
            <div class="score-tile">
              <div class="score-label">Variance</div>
              <div class="score-value" [ngClass]="varianceClass(summary.Variance)">{{ summary.Variance ?? '-' }}</div>
              <div class="score-sublabel">human &ndash; AI</div>
            </div>
          </div>

          <!-- FR-11: computed live, never cached -->
          <h6 class="section-title">Benchmarks</h6>
          <div class="section-subtitle">How this call compares across time and organization</div>
          <div class="score-tiles">
            <div class="score-tile">
              <div class="score-label">Quarterly Avg</div>
              <div class="score-value benchmark-score">{{ summary.Benchmarks?.QuarterlyAverageAiScore | number:'1.0-1' }}</div>
              <div class="score-sublabel">AI, this quarter</div>
            </div>
            <div class="score-tile">
              <div class="score-label">Facility Avg</div>
              <div class="score-value benchmark-score">{{ summary.Benchmarks?.FacilityAverageAiScore | number:'1.0-1' }}</div>
              <div class="score-sublabel">{{ summary.FacilityName }}</div>
            </div>
            <div class="score-tile">
              <div class="score-label">District Avg</div>
              <div class="score-value benchmark-score">{{ summary.Benchmarks?.DistrictAverageAiScore | number:'1.0-1' }}</div>
            </div>
          </div>

          <button *ngIf="!showHumanScoreOverride" type="button" class="btn-override-human-score" (click)="openHumanScoreOverride()">
            <i class="bi bi-sliders2-vertical"></i> Override human score
          </button>

          <div *ngIf="showHumanScoreOverride" class="human-score-override">
            <div class="override-title">Override human score</div>
            <input #scoreInput type="number" class="form-control form-control-sm override-input" min="0" max="100" step="1"
              [(ngModel)]="humanScoreInput" (keydown)="onScoreKeydown($event)" (input)="onScoreInput(scoreInput)"
              (paste)="blockScorePaste($event)" (drop)="blockScorePaste($event)" (blur)="onScoreBlur()" aria-label="Human score" />
            <div class="alert alert-danger score-alert" *ngIf="scoreBlocked" role="alert">Human score ranges between 0-100</div>
            <div class="override-hint">Sets the human review score for this call and marks it Reviewed.</div>
          </div>
        </div>

        <div *ngIf="activeTab === 'transcript'" class="transcript-card">
          <div *ngIf="summary.TranscriptUnavailable || transcriptUnavailable" class="text-muted">Transcript not available for this call.</div>
          <div *ngIf="transcriptLoading" class="text-muted">Loading transcript&hellip;</div>

          <ng-container *ngIf="transcriptLoaded">
            <span class="transcript-chip"><i class="bi bi-chat-square-text"></i> AI Conversation Insight &middot; Transcript</span>
            <div class="transcript-turn" *ngFor="let turn of transcriptTurns; trackBy: trackByIndex">
              <div class="turn-speaker">{{ turn.name }} <span class="turn-time">{{ turn.time }}</span></div>
              <div class="turn-text" [class.agent-turn]="turn.isAgent">{{ turn.text }}</div>
            </div>
          </ng-container>

          <ng-container *ngIf="summary.AiFlagsAndCoaching">
            <h6 class="section-title">Flags &amp; Coaching</h6>
            <p class="pre-wrap">{{ summary.AiFlagsAndCoaching }}</p>
          </ng-container>
        </div>
      </ng-container>
    </div>

    <!-- One Save for the whole panel (#128323) - outside the scrolling body so it stays in view. -->
    <div class="summary-footer sameWidthBtn" *ngIf="!summary.DataExpired">
      <button type="button" class="btn btn-outline-primary me-3" (click)="requestClose()">Cancel</button>
      <button type="button" class="btn btn-primary" [class.is-disabled]="!editRight || !isDirty || saving" [attr.aria-disabled]="!editRight || !isDirty || saving"
        [attr.title]="editRight ? null : 'You do not have Edit Right'" (click)="save()">Save</button>
    </div>
  </ng-container>
</div>

<!-- Same Confirm popup as Payment Settings for unsaved changes. -->
<ng-template #discardChangesModal>
  <div class="modal-header">
    <h5 class="modal-title">Confirm</h5>
    <button type="button" class="close pull-right" aria-label="Close" (click)="keepEditing()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>
  <div class="modal-body ptb24">
    <p class="mb-0">Are you sure you want to cancel? Any data modifications will not be saved</p>
  </div>
  <div class="modal-footer">
    <button class="btn btn-outline-primary me-3" type="button" (click)="keepEditing()">No</button>
    <button class="btn btn-primary" type="button" (click)="discardChanges()">Yes</button>
  </div>
</ng-template>
